// Initial load + optional resume-draft logic for the NewSession page.
//
// Loads: client, protocols, recurring patterns, previous session, AND — when
// `?sid=` is present — fetches that draft session, hydrates every piece of
// state, and picks the right phase under the Iter AE workflow.
//
// Extracted from NewSession.jsx during Iter AN Phase 2. Effect dependencies
// preserved exactly so the load runs exactly once on mount + on (clientId,
// resumeId) change — same behaviour as before.

import { useEffect } from "react";
import { toast } from "sonner";
import { api } from "../../../lib/api";


export function useSessionLoad(deps, setters) {
  const { clientId, resumeId, refreshUser } = deps;
  const {
    setClient, setProtocols, setRecurringPatterns, setPreviousSession,
    setSessionId, setSessionType, setUserOverrodeSessionType,
    setPhotoMode, setComparedFromSessionId, setPhotoComparisonSummary,
    setTreatmentMap, setPhotos, setSessionPhotos, setSignature,
    setIntakeNotes, setAnalysis, setWorkedAreas, setWrapUp,
    setProgressSinceLast, setSoap, setOutcome, setBilling,
    setSelectedProtocolId, setDuration, setClinicalCapture,
    setChiefComplaintObj, setPhase, setSavedAt, setSaveStatus,
    setIncludeRom, setAssessmentMode,
  } = setters;

  useEffect(() => {
    api.get(`/clients/${clientId}`).then((r) => setClient(r.data)).catch(() => toast.error("Client not found"));
    api.get("/protocols").then((r) => setProtocols(r.data)).catch(() => {});
    api.get(`/clients/${clientId}/recurring-patterns?threshold=3`).then((r) => setRecurringPatterns(r.data)).catch(() => {});
    refreshUser?.();
    api.get(`/clients/${clientId}/previous-session`)
      .then((r) => { if (r.data && r.data.id) setPreviousSession(r.data); })
      .catch(() => {});
    if (resumeId) {
      api.get(`/sessions/${resumeId}`).then((r) => {
        const s = r.data;
        setSessionId(s.id);
        if (s.session_type) { setSessionType(s.session_type); setUserOverrodeSessionType(true); }
        if (s.photo_mode) setPhotoMode(s.photo_mode);
        if (s.compared_from_session_id) setComparedFromSessionId(s.compared_from_session_id);
        if (s.photo_comparison_summary) setPhotoComparisonSummary(s.photo_comparison_summary);
        if (s.treatment_map) setTreatmentMap(s.treatment_map);
        setPhotos({ front: s.front_image, side: s.side_image, back: s.back_image, left_side: s.photos?.baseline_left_side || null });
        if (s.photos) setSessionPhotos(s.photos);
        if (s.signature) setSignature(s.signature);
        setIntakeNotes(s.intake_notes || "");
        if (s.ai_analysis) setAnalysis(s.ai_analysis);
        setWorkedAreas(s.worked_areas || []);
        if (s.wrap_up) setWrapUp({
          therapist_summary: s.wrap_up.therapist_summary || "",
          home_care: s.wrap_up.home_care || [],
          best_return: s.wrap_up.best_return || { when: "", why: "" },
          good_return: s.wrap_up.good_return || { when: "", why: "" },
        });
        if (s.progress_since_last) setProgressSinceLast({
          better: s.progress_since_last.better || "",
          worse: s.progress_since_last.worse || "",
          unchanged: s.progress_since_last.unchanged || "",
          new_symptoms: s.progress_since_last.new_symptoms || "",
          resolved_symptoms: s.progress_since_last.resolved_symptoms || "",
          no_significant_changes: !!s.progress_since_last.no_significant_changes,
          benefit_duration: s.progress_since_last.benefit_duration || "",
          new_priority: s.progress_since_last.new_priority || "",
          finding_updates: s.progress_since_last.finding_updates || [],
        });
        if (s.soap) setSoap(s.soap);
        if (s.outcome) setOutcome((o) => ({ ...o, ...s.outcome }));
        if (s.billing) setBilling(s.billing);
        if (s.protocol_id) setSelectedProtocolId(s.protocol_id);
        if (s.duration_minutes) setDuration(s.duration_minutes);
        if (s.clinical_capture) setClinicalCapture(s.clinical_capture);
        setChiefComplaintObj({
          chief_complaint: s.chief_complaint || "",
          chief_complaint_area: s.chief_complaint_area || null,
          secondary_concerns: s.secondary_concerns || [],
          issue_type: s.issue_type || null,
        });
        // ROM is opt-in — re-enable it on resume only if measurements were actually recorded.
        const romActive = (s.outcome?.rom_measurements || []).some(
          (m) => m.before != null || m.after != null || m.quality_before != null || m.quality_after != null
        );
        if (romActive) setIncludeRom?.(true);
        if (s.assessment_mode) setAssessmentMode?.(s.assessment_mode);
        // Land on the most appropriate phase (Iter AU order: reassess → findings → recs → photos → save)
        if (s.status === "completed") setPhase("post-save");
        else if (s.outcome && s.outcome.recommendations) setPhase("eot-recs");
        else if (s.outcome && s.outcome.therapist_findings && (s.outcome.therapist_findings.overall_improvement || s.outcome.therapist_findings.assessment_notes)) setPhase("eot-findings");
        else if (s.outcome && s.outcome.pain_after != null) setPhase("eot-findings");
        else if (s.wrap_up && (s.wrap_up.therapist_summary || "").trim()) setPhase("tx-notes");
        else if ((s.worked_areas || []).length > 0) setPhase("tx-diagram");
        else if (s.ai_analysis) setPhase(romActive ? "pre-rom" : "tx-diagram");
        else if (romActive) setPhase("pre-rom");
        else if (s.chief_complaint_area) setPhase("pre-photos");
        else setPhase("pre-setup");
        setSavedAt(s.updated_at ? new Date(s.updated_at) : null);
        setSaveStatus("saved");
        toast.success("Resumed draft session");
      }).catch(() => toast.error("Could not load draft"));
    }
  }, [clientId, resumeId, refreshUser]);
}
